// Icons for the Relic Vault Store and Vault browser. Paths are taken verbatim
// from the designer's handoff so they render the same as the demo.

import React from 'react';
import { Svg, Path, Circle, Rect } from './svg';

const COIN_SYMBOL =
  'M12 7v10M9 9.5c0-1.2 1.2-2 3-2s3 .9 3 2-1.2 1.6-3 2-3 .8-3 2 1.2 2 3 2 3-.8 3-2';

/** Gold QuikCoin. `light` is the paler face used inside purple buttons. */
export const CoinIcon = ({ size = 18, light = false }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle
      cx="12"
      cy="12"
      r="9"
      fill={light ? '#FFE9A8' : '#F0A93A'}
      stroke="#C97F13"
      strokeWidth="1.5"
    />
    <Path
      d={COIN_SYMBOL}
      stroke="#7A4A0A"
      strokeWidth={light ? '1.1' : '1.3'}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export const VaultChestIcon = ({ size = 17 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M3 10c0-2.8 2.2-5 5-5h8c2.8 0 5 2.2 5 5v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7z"
      fill="#E0B24A"
      stroke="#7A4A0A"
      strokeWidth="1"
    />
    <Path d="M3 10h18" stroke="#7A4A0A" strokeWidth="1.1" />
    <Rect x="10.2" y="10" width="3.6" height="3" rx="0.5" fill="#7A4A0A" />
    <Path
      d="M8 5c0-1.7 1.8-3 4-3s4 1.3 4 3"
      stroke="#C97F13"
      strokeWidth="1.3"
      fill="none"
    />
  </Svg>
);

/** Four-point sparkle flanking section titles. */
export const SparkleIcon = ({ small = false }) => {
  const size = small ? 14 : 18;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
        fill={small ? '#B9A6FF' : '#FFD873'}
      />
    </Svg>
  );
};

export const TorchFlameIcon = () => (
  <Svg width={18} height={22} viewBox="0 0 20 24" fill="none">
    <Path
      d="M8 22c-3 0-5-2-5-5 0-2.5 2-4 2.5-6.5C6 8 6.5 6 8 4c0 2 1 3 2 4.5 1.2 1.8 2 3.3 2 5.5 0 .8-.2 1.5-.5 2 .8-.5 1.5-1.5 1.5-3 0-2-1-3.5-1-5.5 2 1.5 3 4 3 6.5 0 3-2.5 5.5-5.5 5.5H8z"
      fill="#FBBF24"
    />
    <Path
      d="M9 20c-1.3 0-2.3-.9-2.3-2.2 0-1 .6-1.7 1-2.6 .3.8.8 1.2 1.3 1.8.6.7 1 1.3 1 2 0 .6-.4 1-.9 1H9z"
      fill="#F97316"
    />
  </Svg>
);

/** Ornamental gold corner on the vault panel. Mirrored per corner via transforms. */
export const VaultCornerIcon = ({ size = 26 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M3 21 V9 a6 6 0 0 1 6 -6 H21"
      stroke="#D4A853"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <Circle cx="7" cy="7" r="2" fill="#D4A853" />
  </Svg>
);

/** Badge shield, used for the Badges tab. */
export const BadgeShieldIcon = ({ size = 18, color = '#C9C1F0' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <Path d="M12 2.5 19.5 5.5v5.4c0 4.6-3.1 8.6-7.5 10.1-4.4-1.5-7.5-5.5-7.5-10.1V5.5z" />
    <Path d="M12 8.2l1.4 2.9 3.1.4-2.3 2.2.6 3.1-2.8-1.5-2.8 1.5.6-3.1-2.3-2.2 3.1-.4z" />
  </Svg>
);

export const RefreshIcon = ({ size = 15, color = '#B9A6FF' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <Path d="M21 12a9 9 0 1 1-2.6-6.3M21 4v5h-5" />
  </Svg>
);

export const CloseIcon = ({ size = 14, color = '#C9C1F0' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round">
    <Path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const CheckIcon = ({ size = 12, color = '#4ADE80', strokeWidth = '3' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round">
    <Path d="M20 6L9 17l-5-5" />
  </Svg>
);

export const LockIcon = ({ size = 16, color = '#F2EFFC', strokeWidth = '2.4', opacity }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    opacity={opacity}>
    <Rect x="5" y="11" width="14" height="9" rx="2" />
    <Path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Svg>
);

export const SearchIcon = ({ size = 15, color = '#6B6390' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <Circle cx="11" cy="11" r="7" />
    <Path d="M21 21l-4.3-4.3" />
  </Svg>
);

/** 2x2 grid glyph for the "All Relics" sidebar row. */
export const GridIcon = ({ size = 16, color = '#F2EFFC' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2">
    <Rect x="3" y="3" width="7" height="7" rx="1.5" />
    <Rect x="14" y="3" width="7" height="7" rx="1.5" />
    <Rect x="3" y="14" width="7" height="7" rx="1.5" />
    <Rect x="14" y="14" width="7" height="7" rx="1.5" />
  </Svg>
);

export const ChevronLeftIcon = ({ size = 16, color = '#D6C8FF' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round">
    <Path d="M15 18l-6-6 6-6" />
  </Svg>
);

/** "No guardian" glyph. Not in the handoff; added for the equip/none option. */
export const NoGuardianIcon = ({ size = 15, color = '#C9C1F0' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round">
    <Circle cx="12" cy="12" r="9" />
    <Path d="M5.6 5.6l12.8 12.8" />
  </Svg>
);

/** One badge glyph per companion personality - matches the design's icon set. */
export const CompanionIcon = ({ kind, size = 20, color = 'currentColor' }) => {
  const stroked = {
    fill: 'none',
    stroke: color,
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (kind) {
    case 'mountain':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" {...stroked}>
          <Path d="M3 18 L9 8 L13 14 L16 10 L21 18 Z" />
        </Svg>
      );
    case 'flame':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Path d="M12 2c1.2 3.2-1.8 4.3-1.8 7.2a1.8 1.8 0 0 0 3.6 0c0-.9-.4-1.3-.4-1.3 1.8 1 3.2 3.2 3.2 5.4A4.6 4.6 0 0 1 12 18a4.6 4.6 0 0 1-4.6-4.7c0-3.7 2.8-4.6 4.6-11.3z" />
        </Svg>
      );
    case 'moon':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 1 0 10.5 10.5z" />
        </Svg>
      );
    case 'flower':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Circle cx="12" cy="12" r="2.4" />
          <Circle cx="12" cy="5.8" r="2.8" />
          <Circle cx="12" cy="18.2" r="2.8" />
          <Circle cx="5.8" cy="12" r="2.8" />
          <Circle cx="18.2" cy="12" r="2.8" />
        </Svg>
      );
    case 'star':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
        </Svg>
      );
    case 'leaf':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Path d="M5 19c0-8 5-14 15-15 0 10-6 15-15 15z" />
        </Svg>
      );
    case 'gear':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" {...stroked}>
          <Circle cx="12" cy="12" r="3" />
          <Path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 0 1-4 0v-.09A1.7 1.7 0 0 0 9 19.36a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.64 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.64 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.64a1.7 1.7 0 0 0 1-1.55V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.36 9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1z" />
        </Svg>
      );
    case 'book':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" {...stroked}>
          <Path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v15H6.5A2.5 2.5 0 0 0 4 19.5v-15z" />
          <Path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        </Svg>
      );
    default:
      return null;
  }
};
